import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Upload, CloudUpload, RefreshCw, HardDrive, Clock, Shield, Loader2, AlertTriangle } from 'lucide-react';
import { EmptyState } from './shared';
import api from '@/lib/api';
import { toast } from 'sonner';

export function BackupTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [savingR2, setSavingR2] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/backup/list');
      setBackups(data.backups);
    } catch (err) { console.error('Failed to load backups:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBackups(); }, []);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/admin/backup/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `destockpro_backup_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup telecharge');
    } catch { toast.error('Erreur lors du telechargement'); }
    finally { setDownloading(false); }
  };

  const saveToR2 = async () => {
    setSavingR2(true);
    try {
      await api.post('/admin/backup/save-r2');
      toast.success('Backup sauvegarde sur Cloudflare R2');
      loadBackups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSavingR2(false); }
  };

  const handleRestore = async () => {
    if (!selectedFile) { toast.error('Selectionnez un fichier de backup'); return; }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const { data } = await api.post('/admin/backup/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Backup restaure ! ${Object.values(data.stats).reduce((a, b) => a + b, 0)} documents`);
      setConfirmRestore(false);
      setSelectedFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de restauration'); }
    finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Download to PC */}
        <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <Download className="w-5 h-5 text-[#1F3F72]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-neutral-900 dark:text-white">Telecharger</p>
              <p className="text-xs text-neutral-500">Sauvegarder sur votre PC / Google Drive</p>
            </div>
          </div>
          <Button className="w-full bg-[#1F3F72] text-white" onClick={downloadBackup} disabled={downloading} data-testid="backup-download-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Telecharger le backup
          </Button>
        </div>

        {/* Save to R2 */}
        <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center">
              <CloudUpload className="w-5 h-5 text-[#F25C05]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-neutral-900 dark:text-white">Sauvegarder sur le cloud</p>
              <p className="text-xs text-neutral-500">Cloudflare R2 (automatique toutes les 30 min)</p>
            </div>
          </div>
          <Button className="w-full bg-[#F25C05] hover:bg-[#d94f04] text-white" onClick={saveToR2} disabled={savingR2} data-testid="backup-r2-btn">
            {savingR2 ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CloudUpload className="w-4 h-4 mr-2" />}
            Sauvegarder maintenant
          </Button>
        </div>

        {/* Restore */}
        <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-neutral-900 dark:text-white">Restaurer</p>
              <p className="text-xs text-neutral-500">Importer un fichier de backup</p>
            </div>
          </div>
          <label className="block">
            <input
              type="file"
              accept=".json"
              className="block w-full text-xs text-neutral-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 cursor-pointer"
              onChange={(e) => { setSelectedFile(e.target.files[0]); setConfirmRestore(false); }}
              data-testid="backup-file-input"
            />
          </label>
          {selectedFile && !confirmRestore && (
            <Button className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setConfirmRestore(true)} data-testid="backup-restore-btn">
              <Upload className="w-4 h-4 mr-2" /> Restaurer "{selectedFile.name}"
            </Button>
          )}
          {confirmRestore && (
            <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <p className="text-xs font-semibold text-red-600">Attention : cette action remplacera toutes les donnees actuelles !</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleRestore} disabled={restoring} data-testid="backup-confirm-restore-btn">
                  {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />}
                  Confirmer la restauration
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmRestore(false)}>Annuler</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
        <Clock className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Sauvegarde automatique active</p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Backup toutes les 30 minutes (48 derniers conserves = 24h) + 1 backup quotidien (7 derniers = 1 semaine).</p>
        </div>
      </div>

      {/* Backup history */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <HardDrive className="w-4 h-4" /> Historique des backups (Cloudflare R2)
          </h3>
          <Button size="sm" variant="outline" onClick={loadBackups} data-testid="refresh-backups-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Actualiser
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
        ) : backups.length === 0 ? (
          <EmptyState icon={HardDrive} text="Aucun backup sur le cloud" />
        ) : (
          <div className="space-y-2">
            {backups.map((b, i) => (
              <div key={b.key} className="flex items-center justify-between bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-lg px-4 py-3" data-testid={`backup-item-${i}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${b.type === 'quotidien' ? 'bg-blue-50 dark:bg-blue-900/30' : 'bg-emerald-50 dark:bg-emerald-900/30'}`}>
                    <Shield className={`w-4 h-4 ${b.type === 'quotidien' ? 'text-blue-500' : 'text-emerald-500'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">{b.key.split('/').pop()}</p>
                    <p className="text-xs text-neutral-500">
                      {new Date(b.last_modified).toLocaleString('fr-BE')} — {b.size_readable}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {b.type === 'quotidien' && (
                    <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded-full">Quotidien</span>
                  )}
                  {i === 0 && (
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full">Dernier</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
